/**
 * The React side of `proceduralTextureCache`: a mounted component borrows a shared procedural
 * texture by pinning it. Resolving and pinning are one operation, since an overflowing cache
 * disposes an unpinned entry and the consumer, whose memo deps never changed, samples a dead texture.
 * A texture that builds as a worker job is started here, and its slot keeps the texture it
 * had until the new one lands, as Godot keeps the old image while `noise_thread` runs.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { inThreadJobRunner } from '../workers/WorkerJobRunner.js';
import { usePendingWhile } from './usePendingWhile.js';
import { textureWork } from './textures/textureWork.js';
import { useResourceLoader } from './useResource.js';
import { withKey } from '../utils/keySet.js';
import { resolveProceduralTexture, resolveProceduralTexture3D } from './textures/resolveProceduralTexture.js';
import { pinProceduralTexture, releaseProceduralTextures, unpinProceduralTexture, } from './textures/proceduralTextureCache.js';
import { releasedOnUnmount } from '../r3f/sharedTexture.js';
// The cache outlives every canvas, so a canvas that unmounts frees its textures from each renderer.
releasedOnUnmount({ dispose: releaseProceduralTextures });
/**
 * The procedural texture a `SubResource` ref names, held resident while the caller is mounted.
 * Unclaimed for every other form, which the caller's async path handles. `resolveProceduralTexture`
 * decides which types count. A Texture2D slot reads through here or `useTexture2D`, since a
 * path resolver finds no path for a procedural texture.
 */
export function useProceduralTexture(ref, pools, loadedFile) {
    return useProceduralSlots([ref], pools, loadedFile, resolveProceduralTexture)[0];
}
/** {@link useProceduralTexture} for a Texture3D slot, which `useTexture3D` reads through. */
export function useProceduralTexture3D(ref, pools, loadedFile) {
    return useProceduralSlots([ref], pools, loadedFile, resolveProceduralTexture3D)[0];
}
function useProceduralSlots(refs, pools, loadedFile, resolve) {
    // Keyed on the refs' text: callers rebuild the array every render.
    const refsText = JSON.stringify(refs);
    const lookups = useMemo(() => JSON.parse(refsText).map((ref) => resolve(ref ?? undefined, pools, loadedFile)), [refsText, pools, loadedFile, resolve]);
    // Builds that finished after `lookups` read the cache. Scoped to those lookups: a new
    // read of the cache answers for itself, and an entry evicted since is never shown.
    const [landed, setLanded] = useState(NOTHING_LANDED);
    const landedTextures = landed.lookups === lookups ? landed.textures : NOTHING_LANDED.textures;
    const [failedKeys, setFailedKeys] = useState(() => new Set());
    const [buildError, setBuildError] = useState(null);
    if (buildError)
        throw buildError.error;
    const builds = useMemo(() => lookups
        .filter(isPendingBuild)
        .filter((lookup) => !failedKeys.has(lookup.key) && !landedTextures.has(lookup.key)), [lookups, failedKeys, landedTextures]);
    /** Written after each commit: the textures the slots showed, kept while a new build runs. */
    const lastShown = useRef([]);
    const shown = lookups.map((lookup, slot) => {
        if (lookup === null || failedKeys.has(lookup.key))
            return null;
        if (lookup.status === 'ready')
            return { texture: lookup.texture, key: lookup.key };
        const built = landedTextures.get(lookup.key);
        return built ? { texture: built, key: lookup.key } : (lastShown.current[slot] ?? null);
    });
    useEffect(() => {
        lastShown.current = shown;
    });
    const runner = useResourceLoader()?.jobRunner ?? inThreadJobRunner;
    useEffect(() => {
        let current = true;
        const handles = builds.map((build) => {
            const handle = build.start(runner);
            handle.settled.then((texture) => {
                if (!current)
                    return;
                // Null while this effect still holds the build means it could not be allocated.
                if (texture)
                    setLanded((prev) => landOne(prev, lookups, build.key, texture));
                else
                    setFailedKeys((keys) => withKey(keys, build.key));
            }, (error) => {
                if (current)
                    setBuildError({ error });
            });
            return handle;
        });
        return () => {
            current = false;
            handles.forEach((handle) => handle.release());
        };
    }, [builds, lookups, runner]);
    usePendingWhile(builds.length > 0);
    useTextureWorkWhile(builds.length > 0);
    useProceduralTexturePins([
        ...shown.flatMap((entry) => (entry ? [entry.key] : [])),
        ...builds.map((build) => build.key),
    ]);
    return lookups.map((lookup, slot) => ({
        texture: shown[slot]?.texture ?? null,
        claimed: lookup !== null,
        building: lookup !== null && builds.some((build) => build.key === lookup.key),
    }));
}
/**
 * Counts texture work while `isWorking`. A landed build leaves `builds` in the commit that
 * shows its texture, where a draw site's upload starts, so the two effects run in one flush
 * and the count never reads zero between them.
 */
function useTextureWorkWhile(isWorking) {
    useEffect(() => (isWorking ? textureWork.begin() : undefined), [isWorking]);
}
const NOTHING_LANDED = { lookups: null, textures: new Map() };
/** `prev` with one more landed texture, restarted when it answered other lookups. */
function landOne(prev, lookups, key, texture) {
    const textures = new Map(prev.lookups === lookups ? prev.textures : []);
    return { lookups, textures: textures.set(key, texture) };
}
function isPendingBuild(lookup) {
    return lookup?.status === 'pending';
}
/**
 * Holds every key resident while the caller is mounted, for a consumer with several procedural
 * slots. Keyed on the joined keys, not the array: an unpin and pin per render would flush the
 * disposals a pinned replace deferred. A key is `token:subResourceId` or a content key, whose
 * JSON escapes every newline, so it holds none.
 */
export function useProceduralTexturePins(keys) {
    const pinnedKeys = [...new Set(keys)].join('\n');
    useEffect(() => {
        if (!pinnedKeys)
            return undefined;
        const held = pinnedKeys.split('\n');
        held.forEach(pinProceduralTexture);
        return () => held.forEach(unpinProceduralTexture);
    }, [pinnedKeys]);
}
//# sourceMappingURL=useProceduralTexture.js.map